import { type ReactNode, useState } from 'react';

import { PlusIcon } from 'lucide-react';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@nesso/ui/primitives/collapsible';

type Props = {
    label: string;
    disabled?: boolean;
    // Keeps the field open for as long as it is true, and closes it again when it turns false: read
    // it from `formState.defaultValues`, so a save that clears the value collapses the field.
    hasSavedValue?: boolean;
    triggerClassName?: string;
    // Receives whether the therapist opened it, so a field can take focus then but not when it
    // mounted open: bare `autoFocus` would pull focus into the last section of a sheet on open.
    children: ReactNode | ((revealedByClick: boolean) => ReactNode);
};

// Reveal-and-stay: once opened the trigger is gone, so a half-typed value is never tucked out of
// sight while it still blocks the save.
export function FieldReveal({
    label,
    disabled,
    hasSavedValue = false,
    triggerClassName,
    children,
}: Props) {
    const [revealedByClick, setRevealedByClick] = useState(false);
    const isOpen = revealedByClick || hasSavedValue;

    return (
        <Collapsible open={isOpen} onOpenChange={setRevealedByClick}>
            {!isOpen && (
                <CollapsibleTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={disabled}
                        className={cn('-ml-3', triggerClassName)}
                    >
                        <PlusIcon className="size-4" /> {label}
                    </Button>
                </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-collapsible-reveal motion-reduce:animate-none">
                {typeof children === 'function' ? children(revealedByClick) : children}
            </CollapsibleContent>
        </Collapsible>
    );
}
